
import { Button } from "@relume_io/relume-ui";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { BiLogoInstagram, BiLogoWhatsapp, BiMapPin } from "react-icons/bi";
import { toast } from "sonner";
import { contactData } from "../../data/contactData";
import { FeedbackModal } from "../common/FeedbackModal";

import { sendToFormHandler } from "../../services/formService";

export const Footer = React.memo(() => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    type: "success" as "success" | "error",
    title: "",
    message: ""
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || isSubmitting) return;

    setIsSubmitting(true);
    const toastId = toast.loading("Realizando sua inscrição...");

    try {
      await sendToFormHandler({
        formId: "daneluci_newsletter",
        email: email,
      });

      toast.dismiss(toastId);
      setModalConfig({
        isOpen: true,
        type: "success",
        title: "Inscrição Realizada!",
        message: "Seu e-mail foi cadastrado com sucesso! Em breve você receberá nossos conteúdos exclusivos sobre performance."
      });
      setEmail("");
    } catch (error) {
      toast.dismiss(toastId);
      setModalConfig({
        isOpen: true,
        type: "error",
        title: "Erro na Inscrição",
        message: "Não foi possível processar sua inscrição agora. Por favor, tente novamente em alguns instantes."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-[#111111] text-white pt-16 md:pt-24">
      <div className="px-[5%]">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-[1.5fr_1fr_1fr_1.5fr] lg:gap-x-16 lg:gap-y-8">

            {/* Coluna 1: Branding */}
            <div className="flex flex-col items-start gap-6">
              <Link to="/">
                <img loading="lazy"
                  src="https://res.cloudinary.com/tatikka/image/upload/v1765152722/Daneluci_-_Logo_Colorido_2_kix63e.svg"
                  alt="Daneluci Performance"
                  className="h-10 w-auto"
                  width={150}
                  height={40}
                />
              </Link>
              <p className="text-white/75 max-w-xs text-sm leading-relaxed">
                Elevando seu corpo ao próximo nível através do treinamento funcional e alta performance.
              </p>

              <div className="flex flex-col gap-2 text-white/75 text-sm">
                <a
                  href={`tel:+${contactData.phone}`}
                  className="flex items-center gap-2 hover:text-brand-cyan transition-colors"
                >
                  <BiLogoWhatsapp className="size-4 text-brand-cyan" />
                  <span>+55 {contactData.phoneFormatted}</span>
                </a>
                <div className="flex items-center gap-2">
                  <BiMapPin className="size-4 text-brand-cyan" />
                  <span>{contactData.address}</span>
                </div>
                <a
                  href="https://maps.app.goo.gl/53rg7Yf4GFW34biy9"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-cyan hover:underline text-xs ml-6"
                >
                  Ver no mapa
                </a>
              </div>

              <div className="flex gap-4">
                <a href={contactData.instagramBrand} target="_blank" rel="noreferrer" className="size-12 flex items-center justify-center rounded-full bg-white/5 hover:bg-brand-cyan transition-all group" aria-label="Siga-nos no Instagram">
                  <BiLogoInstagram className="size-6 text-white group-hover:text-black" />
                </a>
                <a href={contactData.whatsappLink()} target="_blank" rel="noreferrer" className="size-12 flex items-center justify-center rounded-full bg-white/5 hover:bg-brand-cyan transition-all group" aria-label="Fale conosco no WhatsApp">
                  <BiLogoWhatsapp className="size-6 text-white group-hover:text-black" />
                </a>
              </div>
            </div>

            {/* Coluna 2: Treinos */}
            <div className="flex flex-col gap-6">
              <h3 className="text-xs font-bold uppercase tracking-widest text-brand-cyan/80">Treinos</h3>
              <ul className="flex flex-col gap-4 text-white/75 text-sm">
                <li><Link to="/treinos#funcional" className="hover:text-white transition-colors">Funcional do Movimento</Link></li>
                <li><Link to="/treinos#recuperacao" className="hover:text-white transition-colors">Recuperação de Lesões</Link></li>
                <li><Link to="/treinos#mtb" className="hover:text-white transition-colors">Mountain Bike</Link></li>
                <li><Link to="/treinos#corrida" className="hover:text-white transition-colors">Corrida e Triathlon</Link></li>
              </ul>
            </div>

            {/* Coluna 3: Menu */}
            <div className="flex flex-col gap-6">
              <h3 className="text-xs font-bold uppercase tracking-widest text-brand-cyan/80">Explorar</h3>
              <ul className="flex flex-col gap-4 text-white/75 text-sm">
                <li><Link to="/equipe" className="hover:text-white transition-colors">Nossa Equipe</Link></li>
                <li><Link to="/centro-de-treinamento" className="hover:text-white transition-colors">O Centro</Link></li>
                <li><Link to="/training-camps" className="hover:text-white transition-colors">Training Camps</Link></li>
                <li><Link to="/contato" className="hover:text-white transition-colors">Fale Conosco</Link></li>
                <li><Link to="/politica-de-privacidade" className="hover:text-white transition-colors">Política de Privacidade</Link></li>
              </ul>
            </div>

            {/* Coluna 4: Newsletter */}
            <div className="flex flex-col gap-6 lg:items-center lg:text-center">
              <div className="bg-white/5 p-8 rounded-3xl backdrop-blur-sm border border-white/10 w-full">
                <h3 className="text-xl font-bold mb-2">Receba novidades</h3>
                <p className="text-white/60 text-sm mb-6">Conteúdos exclusivos sobre performance no seu e-mail.</p>
                <form onSubmit={handleSubmit} className="flex flex-col gap-3 sm:flex-row sm:gap-2">
                  <input
                    type="email"
                    name="email"
                    autoComplete="email"
                    required
                    disabled={isSubmitting}
                    placeholder="Seu melhor e-mail"
                    className="flex-1 bg-white/10 border border-white/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-brand-cyan transition-colors disabled:opacity-50"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <Button 
                    variant="secondary" 
                    disabled={isSubmitting}
                    className="bg-brand-cyan border-none text-black hover:bg-brand-cyan/90 rounded-xl px-6 transition-all min-w-[120px] disabled:opacity-50"
                  >
                    {isSubmitting ? "Enviando..." : "Assinar"}
                  </Button>
                </form>
              </div>
            </div>

          </div>

          {/* Bottom Bar */}
          <div className="mt-16 pt-8 pb-12 border-t border-white/10 flex flex-col-reverse gap-6 items-center md:flex-row md:justify-between text-white/60 text-xs">
            <p>© {new Date().getFullYear()} Daneluci Performance. Todos os direitos reservados.</p>
            <div className="flex gap-8">
              <Link to="/politica-de-privacidade" className="hover:text-white transition-colors">Política de Privacidade</Link>
            </div>
          </div>
        </div>
      </div>

      <FeedbackModal 
        isOpen={modalConfig.isOpen}
        onClose={() => setModalConfig(prev => ({ ...prev, isOpen: false }))}
        type={modalConfig.type}
        title={modalConfig.title}
        message={modalConfig.message}
      />
    </footer>
  );
});
